import {
  AlertTriangle,
  Download,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
} from "lucide-react";
import { useEffect, useState } from "react";

import { tokenStorage } from "@/auth/tokenStorage";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { env } from "@/lib/env";
import { formatBytes } from "@/lib/upload";
import { cn } from "@/lib/utils";
import { downloadAttachment, useAttachment } from "../hooks/useAttachments";

interface BaseProps {
  attachmentId: string;
  filename?: string;
  mimeType?: string;
  sizeBytes?: number;
  kind?: "input" | "output";
  className?: string;
}

function iconFor(mime: string) {
  if (mime.startsWith("image/")) return FileImage;
  if (mime === "application/pdf") return FileText;
  if (
    mime === "text/csv" ||
    mime === "application/vnd.ms-excel" ||
    mime ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  )
    return FileSpreadsheet;
  if (
    mime ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    mime === "application/msword"
  )
    return FileText;
  if (
    mime === "text/plain" ||
    mime === "text/markdown" ||
    mime === "text/html" ||
    mime === "application/json"
  )
    return FileText;
  return FileArchive;
}

/**
 * Card compacto para um anexo (input do usuario ou output do agente).
 *
 * - Imagem: thumbnail inline (max-h 240px) via fetch+blob (bucket
 *   privado; <img src> direto nao manda Authorization).
 * - Demais: ícone + nome + tamanho + botão download.
 * - Pending/processing: spinner e botão desabilitado.
 * - Failed: triangulo amarelo + tooltip com erro.
 */
export function AttachmentBubble({
  attachmentId,
  filename,
  mimeType,
  sizeBytes,
  kind = "input",
  className,
}: BaseProps) {
  // Polling SEMPRE ativo -- o ``refetchInterval`` interno do hook
  // desliga sozinho quando o status fica terminal (done/failed/not_needed).
  // Heuristica anterior baseada em mime/size desligava o poll para
  // anexos que vinham com metadados do upload (mas ainda em pending),
  // congelando a UI em "Digerindo..." mesmo apos o worker marcar failed.
  const att = useAttachment(attachmentId, { poll: true });

  const effectiveFilename = att.data?.filename ?? filename ?? "(arquivo)";
  const effectiveMime = att.data?.mime_type ?? mimeType ?? "";
  const effectiveSize = att.data?.size_bytes ?? sizeBytes ?? 0;
  const status = att.data?.digestion_status;
  const error = att.data?.digestion_error ?? null;

  const isImage = effectiveMime.startsWith("image/");
  const isPdf = effectiveMime === "application/pdf";
  const Icon = iconFor(effectiveMime);

  const pending = status === "pending" || status === "processing";
  const failed = status === "failed";

  return (
    <div
      className={cn(
        "flex max-w-md items-center gap-2 rounded-md border border-line bg-surface-0 p-2 text-sm",
        kind === "output" && "border-brand-200 bg-brand-50/30",
        className,
      )}
      role="group"
      aria-label={effectiveFilename}
    >
      {isImage && !pending && !failed ? (
        <AttachmentThumb
          attachmentId={attachmentId}
          alt={effectiveFilename}
        />
      ) : (
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-line bg-surface-1",
            kind === "output" && "border-brand-300 bg-brand-100/60",
          )}
        >
          {pending ? (
            <Loader2 className="h-4 w-4 animate-spin text-ink-500" />
          ) : failed ? (
            <AlertTriangle className="h-4 w-4 text-warning-600" />
          ) : (
            <Icon className="h-4 w-4 text-ink-700" />
          )}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <span
          className="truncate text-xs font-medium text-ink-900"
          title={effectiveFilename}
        >
          {effectiveFilename}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-ink-500">
          {pending
            ? "Digerindo..."
            : failed
              ? "Falha ao processar"
              : effectiveSize > 0
                ? formatBytes(effectiveSize)
                : ""}
        </span>
      </div>
      {failed ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                aria-label="Detalhes do erro"
              >
                <AlertTriangle className="h-4 w-4 text-warning-600" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs text-xs">
                {error || "Não foi possível processar este anexo."}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button
          size="icon"
          variant="ghost"
          disabled={pending}
          onClick={() =>
            void downloadAttachment(attachmentId, effectiveFilename)
          }
          aria-label={
            isPdf
              ? "Baixar PDF"
              : isImage
                ? "Baixar imagem"
                : "Baixar arquivo"
          }
        >
          <Download className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function AttachmentThumb({
  attachmentId,
  alt,
}: {
  attachmentId: string;
  alt: string;
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    void (async () => {
      const token = await tokenStorage.getToken();
      try {
        const resp = await fetch(
          `${env.API_BASE_URL}/attachments/${attachmentId}/download`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!resp.ok || cancelled) return;
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        if (!cancelled) setObjectUrl(url);
      } catch {
        // ignora; mantem spinner
      }
    })();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [attachmentId]);

  if (!objectUrl) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-line bg-surface-1">
        <Loader2 className="h-4 w-4 animate-spin text-ink-500" />
      </div>
    );
  }
  return (
    <img
      src={objectUrl}
      alt={alt}
      className="h-12 w-12 shrink-0 rounded-md border border-line object-cover"
    />
  );
}
